import React, { useState, useEffect } from 'react'
import CleverOfferConfigurator from './cleverOfferConfigurator'
import serverData from 'ppbl/utils/server-data'
import { useQuery, useLazyQuery } from '@apollo/client'
import clientLogger from 'ppbl/utils/client-logger'
import { trackPage } from 'ppbl/utils/fpti-helper'
import {
  getApplicationByPartnerId,
  getOfferConstraintsByPartnerId,
} from 'ppbl/graphql/calculator/queries'
import PageMessage from 'ppbl/components/PageMessage'
import CleverError from './cleverError'

type Props = {
  applicationType: string
}

const CleverCalculatorPage = ({ applicationType }: Props) => {
  const [application, setApplication] = useState()
  const [customOfferConstraints, setCustomOfferConstraints] = useState()
  const [productOfferConstraints, setProductOfferConstraints] = useState()
  const [hasError, setHasError] = useState(false)

  const queryParams = new URLSearchParams(location.search)
  const opportunityId = queryParams.get('opportunityid')

  const userId = serverData?.cleverData?.cleverUserId
  const partnerIdentityAccessToken = serverData.cleverAccessToken

  const [getOffersConstraints] = useLazyQuery(getOfferConstraintsByPartnerId, {
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'no-cache',
    onError(err) {
      clientLogger.cleverError(
        'Error while calling Get Application by Partner Id, redirecting to error page',
        err?.message,
      )
      setHasError(true)
    },
    onCompleted(response) {
      clientLogger.cleverInfo(
        `getOfferConstraintsByPartnerId response successful for opportunityId: ${opportunityId}`,
      )
      const {
        credit: {
          businessCredit: {
            offerConstraints: {
              customOfferConstraints: customOfferData,
              productOfferConstraints: productOfferData,
            },
          },
        },
      } = response
      setCustomOfferConstraints(customOfferData)
      setProductOfferConstraints(productOfferData)
    },
  })

  useQuery(getApplicationByPartnerId, {
    variables: {
      applicationInput: {
        partnerApplicationId: opportunityId,
        partnerIdentityAccessToken,
      },
    },
    skip: application !== undefined,
    onError(err) {
      clientLogger.cleverError(
        'Error while calling Get Application by Partner Id, redirecting to error page',
        err?.message,
      )
      setHasError(true)
    },
    onCompleted(response) {
      clientLogger.cleverInfo(
        `getApplicationByPartnerId response successful for opportunityId: ${opportunityId}`,
      )
      const responseApplication = response?.credit?.businessCredit.application
      setApplication(responseApplication)
      getOffersConstraints({
        variables: {
          offerConstraintsInput: {
            partnerApplicationId: opportunityId,
            partnerIdentityAccessToken,
          },
        },
      })
    },
  })

  useEffect(() => {
    trackPage({ page: 'cleverCalculatorPage' })
    clientLogger.cleverInfo('Clever Calculator Page is loaded')
  }, [])

  if (hasError) {
    return <CleverError />
  }

  if (application && customOfferConstraints) {
    return (
      <CleverOfferConfigurator
        averageMonthlyGrossSales={
          application?.business?.financialProfile?.averageMonthlyGrossSales
        }
        customOfferConstraints={customOfferConstraints}
        productOfferConstraints={productOfferConstraints}
        applicationType={applicationType}
        opportunityId={opportunityId}
        application={application}
        userId={userId}
      />
    )
  }
  return (
    <PageMessage
      type="loading"
      localization={{
        title: 'loading...',
      }}
    />
  )
}

export default CleverCalculatorPage
